// docs/35 — the creatures on the board. Each `wildlife` row is drawn from its pose
// (core/wildlife/motion.ts) at the server clock, so every client shows the same butterfly at the
// same place. Depth follows the isometric rule the units now share: everything in the units
// layer is sorted by where it stands on the ground (its foot y), so a mouse runs behind a
// knight's heels and a butterfly over a goblin's head is in front of it only if it is nearer the
// camera. Things that stand up from the tiles (trees, bushes, walls, statues: drawn in the
// terrain layer, under the light) hide a creature pixel by pixel when they stand nearer the
// camera than it does. Creatures take the local light, vanish into the fog of war where the
// party cannot see, fade out at the board's edge, and fliers cast a small shadow under them.
import type { StandingMask } from '../encounter/EncounterTerrain';
import Phaser from 'phaser';
import type { TileData } from '../../core/sim/types';
import type { TiledBoard } from '../tiling';
import { atlasForFrame } from '../assets';
import { oryx } from '../tiling';
import { PIP, SPIDER_TOP, motes, poseAt, type Pose, type WildRow } from '../../core/wildlife/motion';
import { callsIn } from '../../core/wildlife/calls';
import { playSfx, sfxVariantCount, type SfxKey } from '../../core/audio';
import { WILD_GLOW, WILD_TEXTURE, ensureWildTextures, frameKey, sizeOf, variantOf } from './sprites';

/** A creature takes this share of the local light (units take 0.3: a mouse in the dark is dark). */
const LIGHT_SHARE = 0.6;
const LIGHT_FLOOR = 0.42;
/** Fade at the board's edge over this many board px. */
const EDGE_FADE = 10;

type RGB = readonly [number, number, number];

export interface WildHooks {
  /** The light at a board point in squares (docs/19), or undefined when lighting is off. */
  light?: (cx: number, cy: number) => RGB | undefined;
  /** Whether the party sees a square (docs/31); undefined means a clear field. */
  sees?: (x: number, y: number) => boolean;
  /** Whether the sun casts shadows (day, under the sky). */
  sunny?: () => boolean;
}

interface Drawn {
  img: Phaser.GameObjects.Image;
  /** Spider thread, swarm motes. */
  g?: Phaser.GameObjects.Graphics;
  glow?: Phaser.GameObjects.Image;
  /** Eased visibility (fog, occlusion) so a creature slips out of sight rather than blinking. */
  vis: number;
}

/** Props that lie flat on the ground (flowers, bones, puddles): they never hide anything. */
const FLAT = new Set<string>([
  ...oryx.PROPS.flowers, ...oryx.PROPS.blood, ...oryx.PROPS.slime, ...oryx.PROPS.bones, ...oryx.PROPS.bonePiles, ...oryx.PROPS.puddle,
  ...oryx.PROPS.bloodPool, ...oryx.PROPS.leaves, ...oryx.PROPS.lilyPad, ...oryx.PROPS.starPlant, oryx.PROPS.rubble, oryx.PROPS.paleRubble,
  ...Object.values(oryx.PROPS.traps), ...Object.values(oryx.PROPS.cobweb),
].map((i) => oryx.frame(i as number)));

/** Opaque pixels of what stands up in each square (a cell × cell alpha mask), built per board. */
export class Occluders {
  private masks: Array<Uint8Array | undefined> = [];
  private static pixels = new Map<string, { data: Uint8ClampedArray; w: number }>();

  constructor(private readonly scene: Phaser.Scene, private readonly cell: number, private w = 0, private h = 0) {}

  /** docs/186 EA3: the commissioned art's standing images, bucketed by the squares they cover (instead of the Oryx masks). */
  private standing?: Array<StandingMask[] | undefined>;

  set(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, tiled: TiledBoard | undefined, standing?: readonly StandingMask[]): void {
    this.w = board.w; this.h = board.h;
    this.masks = new Array(board.w * board.h).fill(undefined);
    this.standing = undefined;
    if (standing) {
      const n = this.cell, buckets: Array<StandingMask[] | undefined> = new Array(board.w * board.h).fill(undefined);
      for (const s of standing) {
        const x0 = Math.max(0, Math.floor(s.x / n)), x1 = Math.min(board.w - 1, Math.floor((s.x + s.w - 1) / n));
        const y0 = Math.max(0, Math.floor(s.y / n)), y1 = Math.min(board.h - 1, Math.floor((s.y + s.h - 1) / n));
        for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) (buckets[y * board.w + x] ??= []).push(s);
      }
      this.standing = buckets;
      return;
    }
    if (!tiled) return;
    for (let i = 0; i < board.w * board.h; i++) {
      const t = board.tiles[i], v = tiled.cells[i];
      if (!t || !v) continue;
      const solid = t.terrain === 'Wall' || t.terrain === 'Thicket' || (t.terrain === 'Door' && !t.doorOpen);
      const frames = solid
        ? (v.props.length ? [...v.overlay, ...v.props] : [...v.ground, ...v.overlay, ...v.props])
        : v.props.filter((f) => !FLAT.has(f.frame));
      if (!frames.length) continue;
      const m = new Uint8Array(this.cell * this.cell);
      let any = false;
      for (const f of frames) any = this.stamp(m, atlasForFrame(f.frame), f.frame) || any;
      if (any) this.masks[i] = m;
    }
  }

  private stamp(m: Uint8Array, key: string, name: string): boolean {
    const tm = this.scene.textures;
    if (!tm.exists(key) || !tm.get(key).has(name)) return false;
    const frame = tm.getFrame(key, name);
    let px = Occluders.pixels.get(key);
    if (!px) {
      const src = tm.get(key).getSourceImage() as CanvasImageSource & { width: number; height: number };
      const c = document.createElement('canvas');
      c.width = src.width; c.height = src.height;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      if (!ctx) return false;
      ctx.drawImage(src, 0, 0);
      px = { data: ctx.getImageData(0, 0, c.width, c.height).data, w: c.width };
      Occluders.pixels.set(key, px);
    }
    const n = this.cell;
    let any = false;
    for (let y = 0; y < Math.min(n, frame.cutHeight); y++) for (let x = 0; x < Math.min(n, frame.cutWidth); x++) {
      if (px.data[((frame.cutY + y) * px.w + frame.cutX + x) * 4 + 3]! > 40) { m[y * n + x] = 1; any = true; }
    }
    return any;
  }

  /** Whether something standing nearer the camera than `groundY` covers board pixel (px, py). */
  covers(px: number, py: number, groundY: number): boolean {
    const n = this.cell, cx = Math.floor(px / n), cy = Math.floor(py / n);
    if (cx < 0 || cy < 0 || cx >= this.w || cy >= this.h) return false;
    if (this.standing) {
      // something stands nearer the camera (its foot below the creature's ground) with an opaque pixel there
      for (const s of this.standing[cy * this.w + cx] ?? []) {
        if (s.foot <= groundY) continue;
        const ix = Math.floor(px - s.x), iy = Math.floor(py - s.y);
        if (ix >= 0 && iy >= 0 && ix < s.w && iy < s.h && s.alpha[iy * s.w + ix]! > 40) return true;
      }
      return false;
    }
    if ((cy + 1) * n <= groundY) return false;
    const m = this.masks[cy * this.w + cx];
    if (!m) return false;
    return m[Math.floor(py - cy * n) * n + Math.floor(px - cx * n)] === 1;
  }

  /** Squares with a mask (tests, the review hook). */
  get count(): number { return this.standing ? this.standing.filter(Boolean).length : this.masks.filter(Boolean).length; }
}

export interface WildInfo { rows: number; drawn: number; visible: number; hidden: { fog: number; occluded: number; edge: number }; species: Record<string, number>; occluders: number }

export class WildlifeLayer {
  private drawn = new Map<number, Drawn>();
  private shadows: Phaser.GameObjects.Graphics;
  private occ: Occluders;
  private board = { w: 0, h: 0 };
  private kit = '';
  private on = true;
  private still = false;
  private last?: WildInfo;
  private px: number;
  private now = 0;
  /** The server time the last frame's calls were heard up to (WL10). */
  private heard = 0;

  constructor(private readonly scene: Phaser.Scene, private readonly units: Phaser.GameObjects.Layer, shadowLayer: Phaser.GameObjects.Layer, private readonly cell: number, private hooks: WildHooks = {}) {
    ensureWildTextures(scene);
    this.px = cell / PIP;
    this.shadows = scene.add.graphics();
    shadowLayer.add(this.shadows);
    this.occ = new Occluders(scene, cell);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  setHooks(h: WildHooks): void { this.hooks = h; }

  /** `standing`: the commissioned art's standing images (docs/186 EA3), which the creatures hide behind instead of the Oryx tiles. */
  setBoard(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, tiled: TiledBoard | undefined, kit: string, standing?: readonly StandingMask[]): void {
    this.board = { w: board.w, h: board.h };
    this.kit = kit;
    this.occ.set(board, tiled, standing);
  }

  /** Settings: wildlife on/off, reduce motion (no jitter or wobble). */
  setMode(on: boolean, still: boolean): void {
    this.on = on; this.still = still;
    if (!on) this.clear();
  }

  /** Draws `rows` as they are at server time `now`. */
  update(rows: Iterable<WildRow>, now: number, dtMs: number): void {
    this.shadows.clear();
    this.now = now;
    const info: WildInfo = { rows: 0, drawn: 0, visible: 0, hidden: { fog: 0, occluded: 0, edge: 0 }, species: {}, occluders: this.occ.count };
    this.last = info;
    if (!this.on) return;
    const seen = new Set<number>();
    const ease = Math.min(1, dtMs / 90);
    // WL10: the calls since the last frame (a long gap — a hidden tab — is not replayed)
    const from = now - this.heard > 0 && now - this.heard < 1500 ? this.heard : now;
    this.heard = now;
    for (const row of rows) {
      info.rows++;
      const pose = poseAt(row, now, this.still);
      if (!pose) continue;
      seen.add(row.id);
      info.species[pose.species] = (info.species[pose.species] ?? 0) + 1;
      const d = this.drawn.get(row.id) ?? this.make(row, pose);
      const fogged = info.hidden.fog;
      const target = this.place(d, row, pose, info);
      if (info.hidden.fog === fogged) this.call(row, pose, from, now, target);
      d.vis = d.vis + (target - d.vis) * ease;
      const a = pose.alpha * d.vis;
      d.img.setAlpha(a).setVisible(a > 0.02);
      d.g?.setAlpha(a).setVisible(a > 0.02);
      d.glow?.setAlpha(a * pose.glow * 0.85).setVisible(a > 0.02 && pose.glow > 0.05);
      info.drawn++;
      if (a > 0.3) info.visible++;
    }
    for (const [id, d] of this.drawn) if (!seen.has(id)) { this.drop(d); this.drawn.delete(id); }
  }

  /** Plays the creature's calls in (from, now]: quiet, panned to where it is, softer when out of sight. */
  private call(row: WildRow, pose: Pose, from: number, now: number, vis: number): void {
    if (from >= now) return;
    const w = Math.max(1, this.board.w * PIP);
    const pan = Math.max(-0.9, Math.min(0.9, (pose.x / w) * 2 - 1));
    for (const c of callsIn(row, from, now)) {
      const key = c.bank as SfxKey;
      const n = sfxVariantCount(key);
      if (n) playSfx(key, { variant: c.seed % n, gain: c.gain * (0.45 + 0.55 * Math.min(1, vis)), pan });
    }
  }

  private make(row: WildRow, pose: Pose): Drawn {
    const v = variantOf(pose.species, row.seed, this.kit);
    const img = this.scene.add.image(0, 0, WILD_TEXTURE, frameKey(pose.species, v, pose.frame)).setOrigin(0.5, 1);
    img.setData('variant', v);
    this.units.add(img);
    const d: Drawn = { img, vis: 0 };
    if (pose.species === 'spider' || pose.species === 'gnats') { d.g = this.scene.add.graphics(); this.units.add(d.g); }
    if (pose.species === 'gnats') img.setVisible(false).setAlpha(0);
    if (pose.species === 'firefly') { d.glow = this.scene.add.image(0, 0, WILD_GLOW).setBlendMode(Phaser.BlendModes.ADD); this.units.add(d.glow); }
    this.drawn.set(row.id, d);
    return d;
  }

  /** Positions one creature; returns the visibility it should ease towards (0 hidden, 1 shown). */
  private place(d: Drawn, row: WildRow, p: Pose, info: WildInfo): number {
    const k = this.px;
    const gx = p.x * k, gy = p.y * k, dy = (p.y - p.z) * k;
    const sx = Math.round(gx), sy = Math.round(dy);
    const v = d.img.getData('variant') as number;
    const { w: fw, h: fh } = sizeOf(p.species);
    if (p.species !== 'gnats') d.img.setFrame(frameKey(p.species, v, p.frame)).setPosition(sx, sy).setFlipX(p.face < 0);
    // the units layer sorts by ground y (units at their feet): the creature stands where it stands
    const depth = gy + 0.25;
    d.img.setDepth(depth);
    d.g?.setDepth(depth);
    d.glow?.setDepth(depth + 0.01).setPosition(sx, sy - 1).setScale(0.9 + 0.3 * p.glow);
    if (d.g) {
      d.g.clear();
      if (p.species === 'spider') {
        // the thread: from where it is tied (up the wall's face) to the spider's back
        const top = Math.round((p.y - SPIDER_TOP) * k);
        d.g.lineStyle(1, 0xe8e4dc, 0.35).lineBetween(sx + 0.5, top, sx + 0.5, sy - fh + 1);
      } else {
        d.g.fillStyle(0x24221a, 0.85);
        for (const m of motes(row.seed, this.now, 7, this.still)) {
          d.g.fillRect(Math.round(sx + m.dx * k), Math.round(sy - (m.dz + 1) * k + m.dy * k), 1, 1);
        }
      }
    }
    // a little shadow under anything in the air (by day, outdoors) — at the ground point
    if (p.airborne && p.z > 3 && this.hooks.sunny?.()) {
      const s = Math.max(0.05, 0.22 - p.z * 0.0014);
      const r = Math.max(1, fw * (p.z > 50 ? 0.55 : 0.4));
      this.shadows.fillStyle(0x05070f, s * p.alpha * d.vis);
      this.shadows.fillEllipse(gx, gy, r * 2, Math.max(1, r * 0.7));
    }
    // the local light (docs/19)
    const lit = this.hooks.light?.(gx / this.cell, gy / this.cell - 0.3);
    if (lit && p.species !== 'firefly') {
      const ch = (c: number) => Math.max(LIGHT_FLOOR, 1 - LIGHT_SHARE * (1 - c));
      const tint = (Math.round(ch(lit[0]) * 255) << 16) | (Math.round(ch(lit[1]) * 255) << 8) | Math.round(ch(lit[2]) * 255);
      d.img.setTint(tint);
    } else d.img.clearTint();
    // the board's edge: fade out over the margin (fliers come and go off the board)
    const W = this.board.w * this.cell, H = this.board.h * this.cell;
    const midY = sy - fh / 2;
    const out = Math.max(0, -sx, sx - W, -midY, midY - H);
    let vis = Math.max(0, 1 - out / EDGE_FADE);
    if (vis < 1) info.hidden.edge++;
    // the fog of war (docs/31): only where the party sees the ground under it
    if (this.hooks.sees) {
      const cx = Math.floor(gx / this.cell), cy = Math.floor(gy / this.cell);
      const inside = cx >= 0 && cy >= 0 && cx < this.board.w && cy < this.board.h;
      if (inside && !this.hooks.sees(cx, cy)) { vis = 0; info.hidden.fog++; }
    }
    // standing things nearer the camera hide it, pixel by pixel (where its middle is)
    if (vis > 0 && this.occ.covers(sx, midY, gy)) { vis = 0; info.hidden.occluded++; }
    return vis;
  }

  info(): WildInfo | undefined { return this.last; }

  private drop(d: Drawn): void { d.img.destroy(); d.g?.destroy(); d.glow?.destroy(); }

  clear(): void {
    for (const d of this.drawn.values()) this.drop(d);
    this.drawn.clear();
    this.shadows.clear();
  }

  destroy(): void { this.clear(); this.shadows.destroy(); }
}
